// Components==============
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LetterBlock, {
    LetterBlockProps,
    SoundType,
} from 'components/UI/LetterBlock/LetterBlock';
import { motion } from 'framer-motion';
import React, { createContext, useContext, useState } from 'react';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { useParams } from 'react-router';
import './SoundBar.module.scss';
import { useSelector } from 'react-redux';
// =========================

type Props = {
    index: number;
};

type SoundBarContextTypes = {
    activeTileBarState: SoundType[];
    setActiveTileBarState: React.Dispatch<React.SetStateAction<SoundType[]>>;
    setToggleIsUpdatedOnClick: React.Dispatch<React.SetStateAction<boolean>>;
    userIsTypingAnswer: boolean;
    canClick?: boolean;
};

const SoundBarContext = createContext({} as SoundBarContextTypes);

const DraggableTile = ({
    index,
    soundType,
    letter,
}: Props & LetterBlockProps) => {
    const {
        activeTileBarState,
        setActiveTileBarState,
        setToggleIsUpdatedOnClick,
        userIsTypingAnswer,
        canClick,
    } = useContext(SoundBarContext);

    const [hover, setHover] = useState(false);

    const id = soundType;

    const clickCondition =
        (canClick || activeTileBarState.find((e) => e !== '')) &&
        !userIsTypingAnswer;

    function addToActiveTileBar() {
        if (!clickCondition) return;
        let counter = 0;

        setActiveTileBarState((prev) => {
            const lastItem = prev
                .map((e, i: number) => e !== '' && i)
                .filter((e) => typeof e === 'number') as number[];

            const whereNextItemShouldGo = lastItem[lastItem?.length - 1] + 1;

            if (!whereNextItemShouldGo) {
                return prev.map((e) => {
                    if (e === '' && counter === 0) {
                        setToggleIsUpdatedOnClick((toggle) => !toggle);
                        counter = 1;
                        return id;
                    }
                    return e;
                });
            }

            return prev.map((e, i) => {
                if (i === whereNextItemShouldGo) {
                    setToggleIsUpdatedOnClick((toggle) => !toggle);
                    return id;
                }
                return e;
            });
        });
    }

    return (
        <Draggable draggableId={id} index={index} key={index}>
            {(provided, snapshot) => (
                <>
                    <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={{
                            ...provided.draggableProps.style,
                            transform: snapshot.isDragging
                                ? provided.draggableProps.style?.transform
                                : 'translate(0px, 0px)',
                        }}
                        onClick={addToActiveTileBar}
                        styleName="relative"
                    >
                        {clickCondition && (
                            <motion.div
                                styleName="overlay"
                                animate={{ opacity: hover ? 1 : 0 }}
                                initial={{ opacity: 0 }}
                                style={{
                                    color: soundType.includes('special-sound')
                                        ? 'black'
                                        : 'white',
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} />
                            </motion.div>
                        )}
                        <motion.div
                            styleName="overlay"
                            whileHover={{ opacity: clickCondition ? 0 : 1 }}
                            initial={{ opacity: 1 }}
                            style={{
                                color: 'black',
                            }}
                            onHoverStart={() => setHover(true)}
                            onHoverEnd={() => setHover(false)}
                        >
                            <h3>{letter}</h3>
                        </motion.div>
                        <LetterBlock soundType={soundType} />
                    </div>
                    {snapshot.isDragging && <div styleName="placeholder " />}
                </>
            )}
        </Draggable>
    );
};

export default function SoundBar({
    activeTileBarState,
    setActiveTileBarState,
    setToggleIsUpdatedOnClick,
    userIsTypingAnswer,
    canClick,
}: SoundBarContextTypes) {
    const { treatmentId } = useParams() as { treatmentId: string };
    const treatment = useSelector(
        (store: ReduxStore) => store.treatment?.entities?.byId[treatmentId]
    );
    let withSpecialCharSound = false;
    if (treatment && parseInt(treatment.number) >= 53) {
        withSpecialCharSound = true;
    }

    return (
        <SoundBarContext.Provider
            value={{
                activeTileBarState,
                setActiveTileBarState,
                setToggleIsUpdatedOnClick,
                userIsTypingAnswer,
                canClick,
            }}
        >
            <Droppable droppableId="bar" direction="horizontal">
                {(provided) => (
                    <motion.div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        styleName="wrapper"
                        initial={{ y: 50, opacity: 0 }}
                        exit={{ y: 100, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                    >
                        <DraggableTile index={1} soundType="short-sound" />
                        <DraggableTile index={2} soundType="long-sound" />
                        <DraggableTile index={3} soundType="two-symbol-sound" />
                        <DraggableTile index={4} soundType="special-sound" />
                        <DraggableTile index={6} soundType="consonant" />
                        <DraggableTile index={7} soundType="stupid-sound" />
                        {withSpecialCharSound && (
                            <DraggableTile
                                index={8}
                                soundType="special-character-sound"
                            />
                        )}
                    </motion.div>
                )}
            </Droppable>
        </SoundBarContext.Provider>
    );
}
